import React, { useState, useEffect } from 'react';
import axios from "axios";
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import { Trash2, Plus, Search, X } from 'lucide-react';

import styles from './Tags.module.scss';
import Spinner from '../../components/spinner';
import AddEditPopup from './addEditPopup';
import { useLanguage } from '../../context/languageContext';
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";
import { foldForSearch } from "../../helper/searchFold";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Tags — a keyword attached to zero or more genres. Admins create, edit and
 * remove them here; the genre picker in the modal draws from the genre list.
 */
function Tags() {
    const { adminInfo } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState([]);
    // false = closed, null = create, id = edit that tag.
    const [editing, setEditing] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [search, setSearch] = useState("");

    // Filtered here rather than on the server: every tag is already loaded, so
    // this is instant and needs no round trip — the same reason the Home page
    // searches its posts on the client.
    //
    // Genre names are matched too. The Genres column is half of what this table
    // says, so "marketing" ought to find the tags filed under Marketing & Sales
    // and not just a tag with the word in its name.
    const term = foldForSearch(search.trim());
    const visibleTags = term
        ? tags.filter((tag) =>
            foldForSearch(tag.name).includes(term) ||
            (tag.genres || []).some((g) => foldForSearch(g.genreName).includes(term))
        )
        : tags;

    const fetchTags = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(`${API}/api/admin/tag/getall`, { headers });
            if (response.status) {
                setTags(response?.data || []);
            } else {
                toast.error(response.message || "Could not load tags.");
            }
        } catch (error) {
            console.error("fetchTags catch error::", error);
            toast.error("Could not load tags.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (tag) => {
        const headers = { Authorization: `Bearer ${adminInfo.token}` };
        if (!(await confirmCascadeDelete({ source: "tag", ids: [tag.tagId], what: `the tag "${tag.name}"`, headers }))) return;
        setDeletingId(tag.tagId);
        try {
            const { data: response } = await axios.put(`${API}/api/admin/tag/delete/${tag.tagId}`, {}, { headers });
            if (response.status) {
                toast.success(response.message || `"${tag.name}" deleted.`);
                fetchTags();
            } else {
                toast.error(response.message || "Could not delete the tag.");
            }
        } catch (error) {
            console.error("handleDelete catch error::", error);
            toast.error("Could not delete the tag.");
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        if (adminInfo?.token) fetchTags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo]);

    return (
        <React.Fragment>
            <div className="heading">
                <div>
                    <h4 className="mb-0">
                        Tags{tags.length > 0 ? ` (${term ? `${visibleTags.length} of ${tags.length}` : tags.length})` : ""}
                    </h4>
                    <small className="text-muted">
                        Keywords that can belong to one or more genres — or none at all.
                    </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div className={styles.searchBox}>
                        <Search size={16} className={styles.searchIcon} aria-hidden />
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search tags or genres..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search tags by name or genre"
                        />
                        {term !== "" && (
                            <X
                                size={16}
                                className={styles.searchClear}
                                role="button"
                                aria-label="Clear search"
                                onClick={() => setSearch("")}
                            />
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-dark d-flex align-items-center gap-2"
                        onClick={() => setEditing(null)}
                    >
                        <Plus size={18} /> Add Tag
                    </button>
                </div>
            </div>

            {loading ? (
                <Spinner />
            ) : (
                <div className={`${styles.container} table-responsive mt-1`}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.index}>S.no</th>
                                <th>Tag</th>
                                <th>Genres</th>
                                <th className={styles.action}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleTags.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className={styles.empty}>
                                        {term
                                            ? `No tag or genre matches "${search.trim()}".`
                                            : "No tags yet. Add one to get started."}
                                    </td>
                                </tr>
                            ) : (
                                visibleTags.map((tag, index) => (
                                    <tr key={tag.tagId}>
                                        <td className={styles.index}>{index + 1}</td>
                                        <td className={styles.name}>{tag.name}</td>
                                        <td className={styles.genres}>
                                            {(tag.genres || []).length === 0 ? (
                                                <span className={styles.none}>—</span>
                                            ) : (
                                                <div className={styles.chips}>
                                                    {[...tag.genres]
                                                        .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
                                                        .map((g) => (
                                                            <span
                                                                key={g.genreId}
                                                                className={`${styles.chip} ${g.isPrimary ? styles.primaryChip : ""}`}
                                                                title={g.isPrimary ? "Primary genre" : "Secondary genre"}
                                                            >
                                                                {g.isPrimary ? "★ " : ""}{g.genreName}
                                                            </span>
                                                        ))}
                                                </div>
                                            )}
                                        </td>
                                        <td className={styles.action}>
                                            <div className="d-inline-flex align-items-center gap-1">
                                                <div title="Edit" onClick={() => setEditing(tag.tagId)}>
                                                    <FaEdit size={20} />
                                                </div>
                                                <div
                                                    title="Delete tag"
                                                    className={styles.deleteAction}
                                                    onClick={() => deletingId ? null : handleDelete(tag)}
                                                >
                                                    <Trash2 size={20} />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {editing !== false && (
                <AddEditPopup
                    key={editing ?? "new"}
                    isVisible
                    tagId={editing}
                    handleCloseModal={() => setEditing(false)}
                    onSuccess={fetchTags}
                />
            )}
        </React.Fragment>
    );
}

export default Tags;
